import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  FormControl,
  FormLabel,
  useToast,
  IconButton,
  Badge,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
  Progress,
  Tag,
  TagLabel,
  Heading,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronUpIcon, ChevronDownIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import {
  useTaskHierarchy,
  useCreateSubtask,
  useDeleteSubtask,
  useUpdateSubtaskOrder,
  useUpdateTask,
} from '../hooks/useTasks';
import { useUsers } from '../hooks/useUsers';
import { CreateSubtaskData, Task } from '../api/taskApi';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './PermissionGuard';
import { useOrganizationEnums } from '../hooks/useOrganizationEnums';
import { DEFAULT_ORGANIZATION_ENUMS, formatEnumLabel } from '../utils/organizationEnums';
import BulkSubtaskModal from './modals/BulkSubtaskModal';

interface SubtaskManagementProps {
  taskId: string;
  isParentTask?: boolean;
}

interface SubtaskFormData {
  taskTitle: string;
  priority: string;
  description: string;
  status: string;
  assignedToUser: string[];
  dueDate: string;
}

type UserRef = string | { _id: string; firstName?: string; lastName?: string; email?: string };

const getUserId = (userRef: UserRef): string =>
  typeof userRef === 'string' ? userRef : userRef._id;

const getUserDisplay = (
  userRef: UserRef,
  usersList?: Array<{ _id: string; firstName?: string; lastName?: string; email?: string }>
): string => {
  if (typeof userRef === 'object' && userRef.firstName) {
    const name = `${userRef.firstName} ${userRef.lastName || ''}`.trim();
    return userRef.email ? `${name} (${userRef.email})` : name;
  }

  const userId = getUserId(userRef);
  const user = usersList?.find((u) => u._id === userId);
  if (user) {
    return `${user.firstName} ${user.lastName} (${user.email})`;
  }

  return `User ID: ${userId}`;
};

const SubtaskManagement: React.FC<SubtaskManagementProps> = ({ taskId, isParentTask = false }) => {
  const { user } = useAuth();
  const { canPerformWrites } = usePermissions();
  const { data: organizationEnums } = useOrganizationEnums();
  const taskPriorities = organizationEnums?.taskPriorities ?? DEFAULT_ORGANIZATION_ENUMS.taskPriorities;
  const taskStatuses = organizationEnums?.taskStatuses ?? DEFAULT_ORGANIZATION_ENUMS.taskStatuses;
  const defaultPriority = taskPriorities.includes('medium') ? 'medium' : taskPriorities[0];
  const defaultStatus = taskStatuses.includes('Not Started') ? 'Not Started' : taskStatuses[0];

  const emptyFormData = (): SubtaskFormData => ({
    taskTitle: '',
    priority: defaultPriority,
    description: '',
    status: defaultStatus,
    assignedToUser: [],
    dueDate: '',
  });
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isBulkOpen, onOpen: onBulkOpen, onClose: onBulkClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [editingSubtask, setEditingSubtask] = useState<Task | null>(null);
  const [viewingSubtask, setViewingSubtask] = useState<Task | null>(null);
  const [subtaskToDelete, setSubtaskToDelete] = useState<string | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const [formData, setFormData] = useState<SubtaskFormData>(emptyFormData);

  const [editFormData, setEditFormData] = useState<SubtaskFormData>(emptyFormData);

  // Hooks
  const { data: hierarchy, isLoading: loadingHierarchy } = useTaskHierarchy(taskId);
  const { data: users } = useUsers();
  const createSubtaskMutation = useCreateSubtask(taskId);
  const deleteSubtaskMutation = useDeleteSubtask();
  const updateOrderMutation = useUpdateSubtaskOrder();
  const updateTaskMutation = useUpdateTask();

  const handleInputChange = (field: keyof SubtaskFormData, value: string | string[] | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateSubtask = async () => {
    try {
      const subtaskData: CreateSubtaskData = {
        taskTitle: formData.taskTitle,
        priority: formData.priority,
        description: formData.description,
        status: formData.status,
        assignedToUser: formData.assignedToUser,
        dueDate: formData.dueDate || undefined,
      };

      await createSubtaskMutation.mutateAsync(subtaskData);
      
      toast({
        title: 'Subtask created successfully',
        status: 'success',
        duration: 3000,
      });
      
      // Reset form
      setFormData(emptyFormData());
      
      onClose();
    } catch (error) {
      toast({
        title: 'Error creating subtask',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    setSubtaskToDelete(subtaskId);
    onDeleteOpen();
  };

  const confirmDeleteSubtask = async () => {
    if (!subtaskToDelete) return;
    
    try {
      await deleteSubtaskMutation.mutateAsync(subtaskToDelete);
      toast({
        title: 'Subtask deleted successfully',
        status: 'success',
        duration: 3000,
      });
      setSubtaskToDelete(null);
      onDeleteClose();
    } catch (error) {
      toast({
        title: 'Error deleting subtask',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleEditSubtask = (subtask: Task) => {
    setEditingSubtask(subtask);
    setEditFormData({
      taskTitle: subtask.taskTitle,
      priority: subtask.priority,
      description: subtask.description || '',
      status: subtask.status,
      assignedToUser: (subtask.assignedToUser || []).map((userRef) => getUserId(userRef as UserRef)),
      dueDate: subtask.dueDate ? new Date(subtask.dueDate).toISOString().split('T')[0] : '',
    });
    onEditOpen();
  };

  const handleViewSubtask = (subtask: Task) => {
    setViewingSubtask(subtask);
    onViewOpen();
  };

  const handleUpdateSubtask = async () => {
    if (!editingSubtask) return;
    
    try {
      await updateTaskMutation.mutateAsync({
        id: editingSubtask._id,
        data: {
          taskTitle: editFormData.taskTitle,
          priority: editFormData.priority,
          description: editFormData.description,
          status: editFormData.status,
          assignedToUser: editFormData.assignedToUser,
          dueDate: editFormData.dueDate || undefined,
        },
      });
      
      toast({
        title: 'Subtask updated successfully',
        status: 'success',
        duration: 3000,
      });
      
      setEditingSubtask(null);
      onEditClose();
    } catch (error) {
      toast({
        title: 'Error updating subtask',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleEditInputChange = (field: keyof SubtaskFormData, value: string | string[] | number) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStatusChange = async (subtaskId: string, newStatus: string) => {
    try {
      await updateTaskMutation.mutateAsync({
        id: subtaskId,
        data: { status: newStatus },
      });
      toast({
        title: 'Subtask status updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error updating subtask status',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'not started':
        return 'gray';
      case 'completed':
        return 'green';
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'on hold':
        return 'red';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const isOverdue = (dueDate: string | Date, status: string) => {
    if (!dueDate || status.toLowerCase() === 'completed') return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  const toggleDescriptionExpansion = (subtaskId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subtaskId)) {
        newSet.delete(subtaskId);
      } else {
        newSet.add(subtaskId);
      }
      return newSet;
    });
  };

  const isDescriptionLong = (description: string) => {
    return description.length > 100; // Consider description long if more than 100 characters
  };

  if (loadingHierarchy) {
    return (
      <Box p={4}>
        <Spinner size="md" />
        <Text ml={2}>Loading subtasks...</Text>
      </Box>
    );
  }

  if (!hierarchy || hierarchy.subtasks.length === 0) {
    return (
      <Box bg="white" borderRadius="xl" p={6} boxShadow="md" borderLeft="4px solid" borderLeftColor="purple.400">
        <HStack justify="space-between" mb={4}>
          <Heading size="md" color="purple.700" display="flex" alignItems="center">
            <Box as="span" w={3} h={3} bg="purple.400" borderRadius="full" mr={3} />
            Subtasks
          </Heading>
          {canPerformWrites && (
            <HStack spacing={2}>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="purple"
                size="sm"
                onClick={onOpen}
                borderRadius="full"
              >
                Add Subtask
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="purple"
                onClick={onBulkOpen}
                borderRadius="full"
              >
                Bulk Add
              </Button>
            </HStack>
          )}
        </HStack>
        
        <Box 
          bg="gray.50" 
          p={8} 
          borderRadius="lg" 
          textAlign="center" 
          border="1px solid" 
          borderColor="gray.200"
        >
          <Text color="gray.500" fontSize="md" mb={2}>
            No subtasks yet
          </Text>
          <Text color="gray.400" fontSize="sm">
            Create your first subtask to break down this task into smaller, manageable parts.
          </Text>
        </Box>

        {/* Create Subtask Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create New Subtask</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Subtask Title</FormLabel>
                  <Input
                    value={formData.taskTitle}
                    onChange={(e) => handleInputChange('taskTitle', e.target.value)}
                    placeholder="Enter subtask title"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter subtask description"
                    rows={3}
                  />
                </FormControl>              <HStack width="100%" spacing={4}>
                <FormControl>
                  <FormLabel>Priority</FormLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    {taskPriorities.map((priority) => (
                      <option key={priority} value={priority}>
                        {formatEnumLabel(priority)}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    {taskStatuses.map((status) => (
                      <option key={status} value={status}>
                        {formatEnumLabel(status)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>              <FormControl>
                <FormLabel>Due Date</FormLabel>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                />
              </FormControl>

                <FormControl>
                  <FormLabel>Assign To</FormLabel>
                  <Select
                    placeholder="Select user to assign"
                    value={formData.assignedToUser[0] || ''}
                    onChange={(e) => handleInputChange('assignedToUser', e.target.value ? [e.target.value] : [])}
                  >
                    {users?.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleCreateSubtask}
                isLoading={createSubtaskMutation.isPending}
              >
                Create Subtask
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <BulkSubtaskModal
          isOpen={isBulkOpen}
          onClose={onBulkClose}
          parentTaskId={taskId}
          taskPriorities={taskPriorities}
          taskStatuses={taskStatuses}
          defaultPriority={defaultPriority}
          defaultStatus={defaultStatus}
          users={users}
          onSuccess={(count) => {
            toast({
              title: `${count} subtask${count === 1 ? '' : 's'} created`,
              status: 'success',
              duration: 3000,
            });
          }}
        />
      </Box>
    );
  }

  const completionPercentage = hierarchy.totalSubtasks > 0 
    ? (hierarchy.completedSubtasks / hierarchy.totalSubtasks) * 100 
    : 0;

  return (
    <Box bg="white" borderRadius="xl" p={6} boxShadow="md" borderLeft="4px solid" borderLeftColor="purple.400">
      <HStack justify="space-between" mb={4}>
        <VStack align="start" spacing={1}>
          <Heading size="md" color="purple.700" display="flex" alignItems="center">
            <Box as="span" w={3} h={3} bg="purple.400" borderRadius="full" mr={3} />
            Subtasks ({hierarchy.totalSubtasks})
          </Heading>
          <HStack>
            <Text fontSize="sm" color="gray.500">
              {hierarchy.completedSubtasks} completed
            </Text>
            <Progress
              value={completionPercentage}
              colorScheme="purple"
              size="sm"
              width="100px"
              borderRadius="full"
            />
            <Text fontSize="sm" color="gray.500">
              {Math.round(completionPercentage)}%
            </Text>
          </HStack>
        </VStack>
        {canPerformWrites && (
          <HStack spacing={2}>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="purple"
              size="sm"
              onClick={onOpen}
              borderRadius="full"
            >
              Add Subtask
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={onBulkOpen}
              borderRadius="full"
            >
              Bulk Add
            </Button>
          </HStack>
        )}
      </HStack>

      <VStack spacing={3} align="stretch">
        {hierarchy.subtasks.map((subtask: Task, index: number) => (
          <Box 
            key={subtask._id} 
            bg="white" 
            p={3} 
            borderRadius="md" 
            border="1px solid" 
            borderColor={subtask.dueDate && isOverdue(subtask.dueDate, subtask.status) ? "red.200" : "gray.200"}
            _hover={{ 
              borderColor: subtask.dueDate && isOverdue(subtask.dueDate, subtask.status) ? 'red.300' : 'purple.300', 
              boxShadow: 'sm',
              bg: subtask.description && isDescriptionLong(subtask.description) ? 'purple.25' : 'white'
            }}
            transition="all 0.2s"
            cursor={subtask.description && isDescriptionLong(subtask.description) ? "pointer" : "default"}
            onClick={(e) => {
              // Only handle click if description is long and click is not on interactive elements
              if (subtask.description && isDescriptionLong(subtask.description)) {
                const target = e.target as HTMLElement;
                const isInteractiveElement = target.closest('button, select, input, [role="button"]');
                if (!isInteractiveElement) {
                  toggleDescriptionExpansion(subtask._id);
                }
              }
            }}
          >
            <HStack justify="space-between" align="center">
              {/* Left side - ID, Title, Badges */}
              <HStack spacing={3} flex={1} minW={0}>
                {subtask.description && isDescriptionLong(subtask.description) && (
                  <Box 
                    as="span" 
                    color="purple.400" 
                    fontSize="xs"
                    opacity={0.7}
                    transition="opacity 0.2s"
                    _hover={{ opacity: 1 }}
                  >
                    {expandedDescriptions.has(subtask._id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Box>
                )}
                <Text fontSize="xs" color="gray.500" fontWeight="medium" minW="fit-content" bg={'gray.50'} px={2} py={1} borderRadius="md">
                  {subtask.taskId}
                </Text>
                <Text fontWeight="semibold" fontSize="sm" color="gray.800" noOfLines={1} flex={1}>
                  {subtask.taskTitle}
                </Text>
                <HStack spacing={2} minW="fit-content">
                  <Badge 
                    colorScheme={getStatusColor(subtask.status)}
                    variant="solid"
                    borderRadius="full"
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    {subtask.status}
                  </Badge>
                  <Tag 
                    size="sm" 
                    colorScheme={getPriorityColor(subtask.priority)}
                    borderRadius="full"
                  >
                    <TagLabel fontSize="xs">{subtask.priority}</TagLabel>
                  </Tag>
                  {subtask.dueDate && isOverdue(subtask.dueDate, subtask.status) && (
                    <Badge 
                      colorScheme="red"
                      variant="solid"
                      borderRadius="full"
                      px={2}
                      py={1}
                      fontSize="xs"
                    >
                      OVERDUE
                    </Badge>
                  )}
                </HStack>
              </HStack>

              {/* Right side - Due Date and Actions */}
              <HStack spacing={3} minW="fit-content">
                {subtask.dueDate && (
                  <Text fontSize="xs" color={isOverdue(subtask.dueDate, subtask.status) ? "red.600" : "orange.600"} fontWeight="medium">
                    {new Date(subtask.dueDate).toLocaleDateString()}
                    {isOverdue(subtask.dueDate, subtask.status) && " (Overdue)"}
                  </Text>
                )}
                {canPerformWrites ? (
                  <Select
                    size="xs"
                    value={subtask.status}
                    onChange={(e) => handleStatusChange(subtask._id, e.target.value)}
                    width="110px"
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="xs"
                  >
                    {taskStatuses.map((status) => (
                      <option key={status} value={status}>
                        {formatEnumLabel(status)}
                      </option>
                    ))}
                  </Select>
                ) : null}
                <IconButton
                  icon={<ViewIcon />}
                  size="xs"
                  colorScheme="gray"
                  variant="ghost"
                  onClick={() => handleViewSubtask(subtask)}
                  aria-label="View subtask details"
                  borderRadius="full"
                />
                {canPerformWrites && (
                  <>
                    <IconButton
                      icon={<EditIcon />}
                      size="xs"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => handleEditSubtask(subtask)}
                      aria-label="Edit subtask"
                      borderRadius="full"
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleDeleteSubtask(subtask._id)}
                      aria-label="Delete subtask"
                      borderRadius="full"
                    />
                  </>
                )}
              </HStack>
            </HStack>

            {/* Description - only show if exists and in collapsed state */}
            {subtask.description && (
              <Box mt={2} pl={0}>
                <HStack spacing={2} align="start">
                  <Text 
                    fontSize="xs" 
                    color="gray.600" 
                    noOfLines={expandedDescriptions.has(subtask._id) ? undefined : 1}
                    flex={1}
                  >
                    {subtask.description}
                  </Text>
                  {isDescriptionLong(subtask.description) && (
                    <IconButton
                      size="xs"
                      variant="solid"
                      colorScheme="purple"
                      onClick={() => toggleDescriptionExpansion(subtask._id)}
                      icon={expandedDescriptions.has(subtask._id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      aria-label={expandedDescriptions.has(subtask._id) ? 'Show less' : 'Show more'}
                      minW="auto"
                      h="auto"
                      p={1}
                    />
                  )}
                </HStack>
              </Box>
            )}
          </Box>
        ))}
      </VStack>

      {/* Create Subtask Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader 
            bg="purple.50" 
            borderTopRadius="xl" 
            borderBottom="1px solid" 
            borderColor="purple.200"
          >
            <HStack>
              <Box as="span" w={3} h={3} bg="purple.400" borderRadius="full" />
              <Text>Create New Subtask</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">Subtask Title</FormLabel>
                <Input
                  value={formData.taskTitle}
                  onChange={(e) => handleInputChange('taskTitle', e.target.value)}
                  placeholder="Enter subtask title"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px purple.400' }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter subtask description"
                  rows={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px purple.400' }}
                />
              </FormControl>                <HStack width="100%" spacing={4}>
                  <FormControl>
                    <FormLabel fontWeight="semibold" color="gray.700">Priority</FormLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                    >
                      {taskPriorities.map((priority) => (
                        <option key={priority} value={priority}>
                          {formatEnumLabel(priority)}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontWeight="semibold" color="gray.700">Status</FormLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                    >
                      {taskStatuses.map((status) => (
                        <option key={status} value={status}>
                          {formatEnumLabel(status)}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>                <FormControl>
                  <FormLabel fontWeight="semibold" color="gray.700">Due Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                  />
                </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Assign To</FormLabel>
                <Select
                  placeholder="Select user to assign"
                  value={formData.assignedToUser[0] || ''}
                  onChange={(e) => handleInputChange('assignedToUser', e.target.value ? [e.target.value] : [])}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                >
                  {users?.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="xl">
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleCreateSubtask}
              isLoading={createSubtaskMutation.isPending}
              borderRadius="full"
            >
              Create Subtask
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Subtask Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader 
            bg="blue.50" 
            borderTopRadius="xl" 
            borderBottom="1px solid" 
            borderColor="blue.200"
          >
            <HStack>
              <Box as="span" w={3} h={3} bg="blue.400" borderRadius="full" />
              <Text>Edit Subtask</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">Subtask Title</FormLabel>
                <Input
                  value={editFormData.taskTitle}
                  onChange={(e) => handleEditInputChange('taskTitle', e.target.value)}
                  placeholder="Enter subtask title"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Description</FormLabel>
                <Textarea
                  value={editFormData.description}
                  onChange={(e) => handleEditInputChange('description', e.target.value)}
                  placeholder="Enter subtask description"
                  rows={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                />
              </FormControl>

              <HStack width="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="semibold" color="gray.700">Priority</FormLabel>
                  <Select
                    value={editFormData.priority}
                    onChange={(e) => handleEditInputChange('priority', e.target.value)}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                  >
                    {taskPriorities.map((priority) => (
                      <option key={priority} value={priority}>
                        {formatEnumLabel(priority)}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="semibold" color="gray.700">Status</FormLabel>
                  <Select
                    value={editFormData.status}
                    onChange={(e) => handleEditInputChange('status', e.target.value)}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                  >
                    {taskStatuses.map((status) => (
                      <option key={status} value={status}>
                        {formatEnumLabel(status)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Due Date</FormLabel>
                <Input
                  type="date"
                  value={editFormData.dueDate}
                  onChange={(e) => handleEditInputChange('dueDate', e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Assign To</FormLabel>
                <Select
                  placeholder="Select user to assign"
                  value={editFormData.assignedToUser[0] || ''}
                  onChange={(e) => handleEditInputChange('assignedToUser', e.target.value ? [e.target.value] : [])}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                >
                  {users?.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="xl">
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateSubtask}
              isLoading={updateTaskMutation.isPending}
              borderRadius="full"
            >
              Update Subtask
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Subtask Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="5xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader 
            bg="gray.50" 
            borderTopRadius="xl" 
            borderBottom="1px solid" 
            borderColor="gray.200"
          >
            <HStack>
              <Box as="span" w={3} h={3} bg="gray.400" borderRadius="full" />
              <Text>Subtask Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            {viewingSubtask && (
              <VStack spacing={5} align="stretch">
                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>Task ID</Text>
                  <Text bg="gray.50" p={3} borderRadius="md" fontSize="sm" fontFamily="mono">
                    {viewingSubtask.taskId}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>Title</Text>
                  <Text bg="gray.50" p={3} borderRadius="md">
                    {viewingSubtask.taskTitle}
                  </Text>
                </Box>

                {viewingSubtask.description && (
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Description</Text>
                    <Text bg="gray.50" p={3} borderRadius="md" whiteSpace="pre-wrap">
                      {viewingSubtask.description}
                    </Text>
                  </Box>
                )}

                <HStack spacing={4}>
                  <Box flex={1}>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Status</Text>
                    <Badge 
                      colorScheme={getStatusColor(viewingSubtask.status)}
                      variant="solid"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {viewingSubtask.status}
                    </Badge>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Priority</Text>
                    <Tag 
                      size="md" 
                      colorScheme={getPriorityColor(viewingSubtask.priority)}
                      borderRadius="full"
                    >
                      <TagLabel>{viewingSubtask.priority}</TagLabel>
                    </Tag>
                  </Box>
                </HStack>

                {viewingSubtask.dueDate && (
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Due Date</Text>
                    <HStack>
                      <Text bg="gray.50" p={3} borderRadius="md">
                        {new Date(viewingSubtask.dueDate).toLocaleDateString()}
                      </Text>
                      {isOverdue(viewingSubtask.dueDate, viewingSubtask.status) && (
                        <Badge colorScheme="red" variant="solid" borderRadius="full">
                          OVERDUE
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                )}

                {viewingSubtask.assignedToUser && viewingSubtask.assignedToUser.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Assigned To</Text>
                    <Box bg="gray.50" p={3} borderRadius="md">
                      {viewingSubtask.assignedToUser.map((userRef) => (
                        <Text key={getUserId(userRef as UserRef)}>
                          {getUserDisplay(userRef as UserRef, users)}
                        </Text>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>Created By</Text>
                  <HStack>
                    <Text bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                      {typeof viewingSubtask.createdBy === 'object' && viewingSubtask.createdBy 
                        ? `${viewingSubtask.createdBy.firstName} ${viewingSubtask.createdBy.lastName} (${viewingSubtask.createdBy.email})`
                        : 'Unknown User'
                      }
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {viewingSubtask.createdAt ? new Date(viewingSubtask.createdAt).toLocaleString() : 'N/A'}
                    </Text>
                  </HStack>
                </Box>

                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>Last Updated</Text>
                  <Text bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                    {viewingSubtask.updatedAt ? new Date(viewingSubtask.updatedAt).toLocaleString() : 'N/A'}
                  </Text>
                </Box>

                {/* <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>Debug Info</Text>
                  <Text bg="red.50" p={3} borderRadius="md" fontSize="xs" fontFamily="mono">
                    Status History Length: {viewingSubtask.statusHistory?.length || 0}
                    <br />
                    Raw Status History: {JSON.stringify(viewingSubtask.statusHistory, null, 2)}
                  </Text>
                </Box> */}

                {viewingSubtask.statusHistory && viewingSubtask.statusHistory.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={3}>Status History</Text>
                    <VStack spacing={2} align="stretch">
                      {viewingSubtask.statusHistory
                        .slice()
                        .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
                        .map((history, index) => (
                          <Box 
                            key={index}
                            bg="gray.50" 
                            p={3} 
                            borderRadius="md" 
                            borderLeft="3px solid"
                            borderLeftColor={getStatusColor(history.status) + '.400'}
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={1}>
                                <HStack>
                                  <Badge 
                                    colorScheme={getStatusColor(history.status)}
                                    variant="solid"
                                    size="sm"
                                  >
                                    {history.status}
                                  </Badge>
                                  {history.previousStatus && (
                                    <HStack spacing={1}>
                                      <Text fontSize="xs" color="gray.500">from</Text>
                                      <Badge 
                                        colorScheme="gray"
                                        variant="outline"
                                        size="sm"
                                      >
                                        {history.previousStatus}
                                      </Badge>
                                    </HStack>
                                  )}
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                  Changed by: {typeof history.changedBy === 'object' && history.changedBy 
                                    ? `${history.changedBy.firstName} ${history.changedBy.lastName}`
                                    : 'Unknown User'
                                  }
                                </Text>
                              </VStack>
                              <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                {new Date(history.changedAt).toLocaleString()}
                              </Text>
                            </HStack>
                          </Box>
                        ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="xl">
            <Button variant="ghost" onClick={onViewClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                if (viewingSubtask) {
                  handleEditSubtask(viewingSubtask);
                  onViewClose();
                }
              }}
              ml={3}
              leftIcon={<EditIcon />}
            >
              Edit Subtask
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Subtask
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this subtask? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={confirmDeleteSubtask}
                ml={3}
                isLoading={deleteSubtaskMutation.isPending}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <BulkSubtaskModal
        isOpen={isBulkOpen}
        onClose={onBulkClose}
        parentTaskId={taskId}
        taskPriorities={taskPriorities}
        taskStatuses={taskStatuses}
        defaultPriority={defaultPriority}
        defaultStatus={defaultStatus}
        users={users}
        onSuccess={(count) => {
          toast({
            title: `${count} subtask${count === 1 ? '' : 's'} created`,
            status: 'success',
            duration: 3000,
          });
        }}
      />
    </Box>
  );
};

export default SubtaskManagement;
